// src/pages/etc/TotalStatus.jsx
import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import api from "../../api";
import styles from "./TotalStatus.module.css";

// ─── 유틸 ────────────────────────────────────────────────────────────────────
const parseAmount = (v) => {
  const n = Number(String(v ?? "0").replace(/,/g, ""));
  return Number.isFinite(n) ? n : 0;
};
const fmt = (n) => Number(n || 0).toLocaleString();

// ─── 사업부문 ─────────────────────────────────────────────────────────────────
const UNITS = [
  { key: "경영팀",      label: "경영팀(본사)",   expUrl: "/expense/business/" },
  { key: "용역",        label: "용역사업",        expUrl: "/expense/service/" },
  { key: "환경/소독",   label: "환경/소독",       expUrl: "/expense/env/disinfection/" },
  { key: "환경/종합",   label: "환경/종합",       expUrl: "/expense/env/total/" },
  { key: "카페_가산",   label: "카페인 가산점",   expUrl: "/expense/cafe/gasan/" },
  { key: "카페_독산",   label: "카페인 독산점",   expUrl: "/expense/cafe/doksan/" },
  { key: "카페_금나래", label: "카페인 금나래점", expUrl: "/expense/cafe/geumnarae/" },
  { key: "카페_그린",   label: "카페인 그린점",   expUrl: "/expense/cafe/green/" },

];

// ─── 오른쪽 패널 컬럼 정의 (title 키워드 매칭) ───────────────────────────────
const RIGHT_COLS = [
  { key: "salary",    label: "급여",        match: (t = "") => t.includes("급여") },
  { key: "insurance", label: "사회보험료",  match: (t = "") => t.includes("사회보험료") },
  { key: "pension",   label: "퇴직연금",    match: (t = "") => t.includes("퇴직연금") },
  { key: "meeting",   label: "간담회비",    match: (t = "") => t.includes("간담회") },
  { key: "vat1",      label: "부가세(1기)", match: (t = "") => t.includes("부가세") && t.includes("1기") },
  { key: "vat2",      label: "부가세(2기)", match: (t = "") => t.includes("부가세") && t.includes("2기") },
  { key: "material",  label: "재료비",      match: (t = "") => t.includes("재료비") },
  { key: "supplies",  label: "소모품",      match: (t = "") => t.includes("소모품") },
  { key: "goods",     label: "상품매입",    match: (t = "") => t.includes("상품매입") },
  { key: "fuel",      label: "차량유류비",  match: (t = "") => t.includes("유류비") },
  { key: "installment", label: "할부금",    match: (t = "") => t.includes("할부금") },
];

// ─── fetch ────────────────────────────────────────────────────────────────────
const fetchIncomeSum = async (url, params) => {
  let sum = 0, page = 1;
  while (page <= 200) {
    try {
      const res  = await api.get(url, { params: { ...params, page } });
      const list = Array.isArray(res.data) ? res.data : res.data?.results ?? [];
      list.forEach((item) => { sum += parseAmount(item.amount); });
      if (!res.data?.next || list.length === 0) break;
      page++;
    } catch { break; }
  }
  return sum;
};

const fetchAllExpense = async (url, params) => {
  let all = [], page = 1;
  while (page <= 200) {
    try {
      const res  = await api.get(url, { params: { ...params, page } });
      const list = Array.isArray(res.data) ? res.data : res.data?.results ?? [];
      all = all.concat(list);
      if (!res.data?.next || list.length === 0) break;
      page++;
    } catch { break; }
  }
  return all;
};

// ─── 레코드 배열 → 지출합계 + 오른쪽 패널 값 ────────────────────────────────
const computeFromRecords = (records) => {
  const result = { expenseTotal: 0 };
  RIGHT_COLS.forEach((col) => { result[col.key] = 0; });

  records.forEach((r) => {
    const rowTotal = parseAmount(r.supply_amount) + parseAmount(r.vat);
    result.expenseTotal += rowTotal;
    const title = r.title || "";
    RIGHT_COLS.forEach((col) => {
      if (col.match(title)) result[col.key] += rowTotal;
    });
  });
  return result;
};

// ─── CSS grid 컬럼 정의 ───────────────────────────────────────────────────────
// 구분(160px) + 수입/지출/차액(140px×3) + RIGHT_COLS(120px×6)
const GRID_TEMPLATE =
  `160px repeat(3, 140px) repeat(${RIGHT_COLS.length}, 120px)`;
const GRID_COLS = 1 + 3 + RIGHT_COLS.length;

// ─── 컴포넌트 ────────────────────────────────────────────────────────────────
export default function TotalStatus() {
  const year = new Date().getFullYear();
  const [fromDate, setFromDate] = useState(`${year}-01-01`);
  const [toDate,   setToDate]   = useState(`${year}-12-31`);
  const [loading,  setLoading]  = useState(false);
  const [income,    setIncome]    = useState({});
  const [expense,   setExpense]   = useState({});
  const [breakdown, setBreakdown] = useState({});

  useEffect(() => {
    (async () => {
      setLoading(true);
      const dp = { start_date: fromDate, end_date: toDate };


      // 수입
      const [incB, incS, incD, incE, incG, incDk, incGn, incGr] = await Promise.all([
        fetchIncomeSum("/income/business/",       dp),
        fetchIncomeSum("/income-service/",        dp),
        fetchIncomeSum("/income-drafts/",         { ...dp, category: "환경/소독" }),
        fetchIncomeSum("/env-total-incomes/",     dp),
        fetchIncomeSum("/income/cafe/gasan/",     dp),
        fetchIncomeSum("/income/cafe/doksan/",    dp),
        fetchIncomeSum("/income/cafe/geumnarae/", dp),
        fetchIncomeSum("/income/cafe/green/",     dp),
      ]);
      setIncome({
        "경영팀": incB, "용역": incS, "환경/소독": incD, "환경/종합": incE,
        "카페_가산": incG, "카페_독산": incDk, "카페_금나래": incGn, "카페_그린": incGr,
      });



      // 지출: raw 레코드 → 지출합계 + 오른쪽 패널 동시 계산
      const allRecords = await Promise.all(
        UNITS.map((u) => fetchAllExpense(u.expUrl, dp))
      );
      const expObj = {}, breakObj = {};
      UNITS.forEach((u, i) => {
        const c = computeFromRecords(allRecords[i]);
        expObj[u.key]   = c.expenseTotal;
        breakObj[u.key] = c;
      });
      setExpense(expObj);
      setBreakdown(breakObj);
      setLoading(false);
    })();
  }, [fromDate, toDate]);

  // 합계
  const totalIncome  = UNITS.reduce((s, u) => s + (income[u.key]  || 0), 0);
  const totalExpense = UNITS.reduce((s, u) => s + (expense[u.key] || 0), 0);
  const totalDiff    = totalIncome - totalExpense;
  const colTotals    = {};
  RIGHT_COLS.forEach((col) => {
    colTotals[col.key] = UNITS.reduce((s, u) => s + (breakdown[u.key]?.[col.key] || 0), 0);
  });

  // ── 엑셀 다운로드 ──────────────────────────────────────────────────────────
  const downloadExcel = () => {
    const header = [
      "구분", "수입(청구)", "지출(집행)", "차액",
      ...RIGHT_COLS.map((c) => c.label),
    ];
    const dataRows = UNITS.map((unit) => {
      const inc  = income[unit.key]  || 0;
      const exp  = expense[unit.key] || 0;
      const diff = inc - exp;
      const bd   = breakdown[unit.key] || {};
      return [unit.label, inc, exp, diff, ...RIGHT_COLS.map((col) => bd[col.key] || 0)];
    });
    const totalRow = [
      "합 계", totalIncome, totalExpense, totalDiff,
      ...RIGHT_COLS.map((col) => colTotals[col.key] || 0),
    ];
    const wsData = [header, ...dataRows, totalRow];
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws["!cols"] = [{ wch: 16 }, { wch: 16 }, { wch: 16 }, { wch: 16 }, ...RIGHT_COLS.map(() => ({ wch: 14 }))];
    for (let r = 1; r < wsData.length; r++) {
      for (let c = 1; c < header.length; c++) {
        const addr = XLSX.utils.encode_cell({ r, c });
        if (ws[addr]) ws[addr].z = "#,##0";
      }
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "수입지출현황");
    XLSX.writeFile(wb, `수입지출현황_${fromDate}_${toDate}.xlsx`);
  };

  return (
    <div className={styles.wrap}>
      {/* 필터 */}
      <div className={styles.filterBar}>
        <label>기간</label>
        <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className={styles.dateInput} />
        <span className={styles.dateDash}>~</span>
        <input type="date" value={toDate}   onChange={(e) => setToDate(e.target.value)}   className={styles.dateInput} />
        <button
          onClick={downloadExcel}
          disabled={loading}
          style={{
            marginLeft: 8,
            padding: "3px 8px",
            background: loading ? "#9ca3af" : "#16a34a",
            color: "#fff",
            border: "none",
            borderRadius: 4,
            fontSize: 12,
            fontWeight: 600,
            cursor: loading ? "not-allowed" : "pointer",
            whiteSpace: "nowrap",
          }}
        >
          📥 엑셀 다운로드
        </button>
      </div>

      {/* 통합 그리드 */}
      <div
        className={styles.tableWrap}
        style={{ display: "grid", gridTemplateColumns: GRID_TEMPLATE }}
      >
        {/* ── 헤더 ── */}
        <div className={styles.headerCell}>구분</div>
        <div className={styles.headerCell}>수입(청구)</div>
        <div className={styles.headerCell}>지출(집행)</div>
        <div className={styles.headerCell}>차액</div>
        {/* 구분선: 오른쪽 패널 시작 표시 */}
        {RIGHT_COLS.map((col, i) => (
          <div
            key={col.key}
            className={`${styles.headerCell} ${styles.subHeader}`}
            style={i === 0 ? { borderLeft: "2px solid #cbd5e1" } : {}}
          >
            {col.label}
          </div>
        ))}

        {/* ── 로딩 ── */}
        {loading && (
          <div className={styles.loading} style={{ gridColumn: `1 / ${GRID_COLS + 1}` }}>
            불러오는 중...
          </div>
        )}

        {/* ── 데이터 행 ── */}
        {!loading && UNITS.map((unit) => {
          const inc  = income[unit.key]  || 0;
          const exp  = expense[unit.key] || 0;
          const diff = inc - exp;
          const bd   = breakdown[unit.key] || {};
          return (
            <React.Fragment key={unit.key}>
              <div className={styles.cell}>{unit.label}</div>
              <div className={`${styles.cellRight} ${styles.incomeColor}`}>{fmt(inc)} 원</div>
              <div className={`${styles.cellRight} ${styles.expenseColor}`}>{fmt(exp)} 원</div>
              <div className={`${styles.cellRight} ${diff >= 0 ? styles.plus : styles.minus}`}>
                {diff >= 0 ? "+" : ""}{fmt(diff)} 원
              </div>
              {RIGHT_COLS.map((col, i) => (
                <div
                  key={col.key}
                  className={`${styles.cellRight} ${styles.subColor}`}
                  style={i === 0 ? { borderLeft: "2px solid #cbd5e1" } : {}}
                >
                  {fmt(bd[col.key] || 0)} 원
                </div>
              ))}
            </React.Fragment>
          );
        })}

        {/* ── 합계 행 ── */}
        {!loading && (
          <React.Fragment>
            <div className={`${styles.totalRow} ${styles.cell}`}>합 계</div>
            <div className={`${styles.totalRow} ${styles.cellRight} ${styles.incomeColor}`}>{fmt(totalIncome)} 원</div>
            <div className={`${styles.totalRow} ${styles.cellRight} ${styles.expenseColor}`}>{fmt(totalExpense)} 원</div>
            <div className={`${styles.totalRow} ${styles.cellRight} ${totalDiff >= 0 ? styles.plus : styles.minus}`}>
              {totalDiff >= 0 ? "+" : ""}{fmt(totalDiff)} 원
            </div>
            {RIGHT_COLS.map((col, i) => (
              <div
                key={col.key}
                className={`${styles.totalRow} ${styles.cellRight} ${styles.subColor}`}
                style={i === 0 ? { borderLeft: "2px solid #cbd5e1" } : {}}
              >
                {fmt(colTotals[col.key] || 0)} 원
              </div>
            ))}
          </React.Fragment>
        )}
      </div>
    </div>
  );
}